import { type ThemeConfig, extendTheme } from '@chakra-ui/react'

import { animations } from './animations'
import { colors } from './colors'
import { avatarTheme } from './components/avatar'
import { badgeTheme } from './components/badge'
import { baseInputTheme } from './components/baseInput'
import { buttonTheme } from './components/button'
import { cardTheme } from './components/cards'
import { checkboxTheme } from './components/checkbox'
import { drawerTheme } from './components/drawer'
import { formLabelTheme } from './components/formLabelTheme'
import { formTheme } from './components/formTheme'
import { headerTheme } from './components/header'
import { inputTheme } from './components/input'
import { modalTheme } from './components/modal'
import { numberInputTheme } from './components/number-input'
import { PageHeaderContainerTheme } from './components/pageHeaderContainerTheme'
import { selectTheme } from './components/select'
import { selectInputTheme } from './components/selectInputTheme'
import { tabsTheme } from './components/tabs'
import { textStyles, textTheme } from './components/text'
import { textareaTheme } from './components/textarea'
import { tooltipTheme } from './components/tooltip'
import { shadows } from './shadows'

const scrollbar = {
  '::-webkit-scrollbar': {
    display: 'none',
  },
  '.simplebar-track.simplebar-vertical': {
    width: '16px',
  },
  '.simplebar-scrollbar::before': {
    backgroundColor: 'presets.neutral.60',
    borderRadius: '42px',
  },
  '.simplebar-scrollbar.simplebar-visible::before': {
    opacity: 0.6,
  },
  '.simplebar-scrollbar.simplebar-visible.simplebar-hover::before': {
    opacity: 0.8,
  },
  '.simplebar-content': {
    height: '100%',
  },
  '.ag-body-horizontal-scroll.ag-scrollbar-invisible, .ag-body-horizontal-scroll.ag-scrollbar-invisible.ag-apple-scrollbar, .ag-body-vertical-scroll.ag-scrollbar-invisible, .ag-body-vertical-scroll.ag-scrollbar-invisible.ag-apple-scrollbar':
    {
      opacity: 0.6,
      visibility: 'visible',
      '&:hover': {
        opacity: 0.8,
      },
    },
  '.ag-root': {
    '& ::-webkit-scrollbar': {
      display: 'block',
      appearance: 'none',
      width: '16px',
      height: '16px',
    },
    '& ::-webkit-scrollbar-thumb': {
      backgroundColor: 'presets.neutral.60',
      borderRadius: '42px',
      border: '2px solid transparent',
      /* Needed in combination with border to add space around the bar,
      padding/margin do not work. */
      backgroundClip: 'padding-box',
      opacity: 0.6,
      '&:hover': {
        opacity: 0.8,
      },
    },
  },
}

export const theme: ThemeConfig = extendTheme({
  fonts: {
    heading: `'Montserrat', sans-serif`,
    body: `'Montserrat', sans-serif`,
  },
  textStyles,
  components: {
    Avatar: avatarTheme,
    Badge: badgeTheme,
    Button: buttonTheme,
    Card: cardTheme,
    Checkbox: checkboxTheme,
    Drawer: drawerTheme,
    SelectInput: selectInputTheme,
    Heading: headerTheme,
    Input: inputTheme,
    BaseInput: baseInputTheme,
    Modal: modalTheme,
    NumberInput: numberInputTheme,
    PageHeaderContainer: PageHeaderContainerTheme,
    Tabs: tabsTheme,
    Text: textTheme,
    Textarea: textareaTheme,
    Tooltip: tooltipTheme,
    Select: selectTheme,
    Form: formTheme,
    FormLabel: formLabelTheme,
  },
  styles: {
    global: {
      'body, html, #root, .App': {
        bg: 'body.bg',
        width: '100%',
        height: '100%',
        boxSize: 'border-box',
        letterSpacing: '0.2px',
        fontSize: '16px',
        fontFamily: `'Montserrat', sans-serif`,
      },
      'a, a:hover': {
        textDecoration: 'none !important',
      },
      button: {
        border: 'none',
        background: 'transparent',
        margin: '0',
        padding: '0',
        cursor: 'pointer',
      },
      ...animations,
      'input:focus': {
        outline: 'none !important',
      },
      ...scrollbar,
    },
  },
  colors,
  shadows: {
    ...shadows,
    outline: 'none',
  },
})

export default theme

declare module '@chakra-ui/react' {
  export interface ChakraProps {
    'data-testid'?: string
  }
}
